import { useState, useEffect, useRef, useMemo } from 'react';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import noop from 'utils/noop';
import * as k from 'constants/keyCodes';
import * as c from 'utils/fieldConstants';
import { EXIT_RULE_ID } from 'utils/fieldConstants';
import { MultiSelectTrigger } from './MultiSelectTrigger';
import {
  getIsValueInArray,
  getNextIndex,
  scrollChildIntoView,
  toggleValueInArray,
  getSelectedValueNamesInArray,
  getExitRuleValueNameInArray,
} from './MultiSelectDropdown.utils';
import {
  StyledMultiSelectDropdown,
  StyledCheckableOption,
  SecondaryOptionText,
  StyledTopTooltip,
  StyledOptionContainer,
} from './MultiSelectDropdown.styles';
import { useGetCampaignsSelectOptions } from './useGetCampaignsSelectOptions';
import { useGetJourneyMetadataSelectOptions } from './useGetJourneyMetadataSelectOptions';
import { useGetExitRulesSelectOptions } from './useGetExitRulesSelectOptions';

export const MULTI_SELECT_TEST_ID = 'multi-select-test-id';
export const MULTI_SELECT_MENU_TEST_ID = 'multi-select-menu-test-id';
export const MULTI_SELECT_INPUT_TEST_ID = 'multi-select-input-test-id';

// export interface MultiSelectDropdownProps extends BaseFieldProps {
//   /** Name of the class - used to override styles */
//   className?: string;
//   /** Options to default to if gql query fails */
//   defaultOptions?: any[];
//   /** The field used in this dropdown */
//   field: $Values<MULTI_SELECT_FIELDS>;
//   /** The selected values */
//   values: string[];
//   disabled?: boolean;
//   /** read only view prop */
//   isReadOnly?: boolean;
// }

const MultiSelectDropdown = ({
  className,
  defaultOptions = [],
  disabled = false,
  isReadOnly = false,
  error,
  field,
  fieldIdentifier = [],
  onChange = noop,
  values: baseValues = [],
  value,
  hookfn,
  shouldUseLocalSearch = false,
}) => {
  // this is a workaround that if we are converting a previous input type that used value and we move over to the multiselect, the value will be populated.  When we save, the value is dropped and it will then be values.
  const values = useMemo(() => {
    if (!!value) {
      return [...baseValues, value];
    }
    return baseValues;
  }, [value, baseValues]);

  const [isOpen, setIsOpen] = useState(false);
  const [tooltipContent, setTooltipContent] = useState();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [debouncedSearch, search, setSearch] = useDebouncedState('', 300);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const {
    options: baseOptions,
    hasError,
    isLoading,
    runQuery,
    fieldName,
    selectPlaceHolder,
  } = hookfn({
    defaultOptions,
    field,
    values,
  });

  const options = useMemo(() => {
    if (shouldUseLocalSearch) {
      return baseOptions.filter((option) =>
        option.name.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return baseOptions;
  }, [baseOptions, search, shouldUseLocalSearch]);

  const getDropdownRef = () => dropdownRef?.current?.ref?.current;

  const getIsItemActive = ({ value: v }) => getIsValueInArray(v, values);

  const handleItemToggle = (item) => {
    onChange(toggleValueInArray(item?.value, values), ...fieldIdentifier, 'values');
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const openDropdown = () => setIsOpen(true);
  const toggleDropdown = () => (isOpen ? closeDropdown() : openDropdown());

  const handleClick = (e) => {
    if (!e) return;
    // only toggle if the item which was clicked is not in the menu
    if (!menuRef.current.contains(e?.target)) {
      toggleDropdown();
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleBlur = (e) => {
    if (!e) return;
    // only toggle if the item which is focused is not in the menu
    if (!menuRef.current.contains(e.relatedTarget)) {
      closeDropdown();
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const scrollSelectedItemIntoView = () => {
    if (!menuRef.current) return;

    const innerMenu = menuRef.current.querySelector('.menu.visible.inner');

    if (!innerMenu) return;

    const item = innerMenu.querySelector('.item.selected');

    if (!item) return;

    scrollChildIntoView(item, innerMenu);
  };

  const moveSelectionBy = (offset = 0) => {
    if (offset === 0 || !options?.length) return;

    setSelectedIndex(getNextIndex(options, { offset, currentIndex: selectedIndex }));
  };

  const handleKeyDown = (e) => {
    if (!e) return;

    const { keyCode } = e;

    switch (keyCode) {
      case k.SPACE_KEY:
        if (!isOpen) {
          openDropdown();
        }
        break;
      case k.ESCAPE_KEY:
        closeDropdown();
        break;
      case k.ENTER_KEY:
        handleItemToggle(options[selectedIndex]);
        break;
      case k.DOWN_ARROW:
        moveSelectionBy(1);
        break;
      case k.UP_ARROW:
        moveSelectionBy(-1);
        break;
      case k.HOME:
        setSelectedIndex(0);
        e.preventDefault();
        break;
      case k.END:
        setSelectedIndex(options?.length ? options.length - 1 : -1);
        e.preventDefault();
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (!shouldUseLocalSearch) {
      runQuery({ variables: { search: debouncedSearch } });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!e || !isOpen) return;
      const ref = getDropdownRef();
      if (!ref) return;
      // Make sure you're not clicking *inside* the dropdown
      if (!ref.contains(e.target)) {
        closeDropdown();
        document.removeEventListener('click', handleDocumentClick);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => {
      if (isOpen) {
        document.removeEventListener('click', handleDocumentClick);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    runQuery();
  }, [field]);

  useEffect(() => {
    let focusTimeOut = null;

    if (isOpen) {
      setSelectedIndex(0);
      focusTimeOut = setTimeout(() => {
        if (inputRef?.current) {
          inputRef?.current?.focus();
        }
      }, 0);
    }

    return () => {
      if (focusTimeOut) {
        clearTimeout(focusTimeOut);
      }
    };
  }, [isOpen, inputRef]);

  useEffect(() => {
    scrollSelectedItemIntoView();
  }, [selectedIndex]);

  useEffect(() => {
    if (options.length && values.length && isReadOnly) {
      const content =
        field === EXIT_RULE_ID
          ? getExitRuleValueNameInArray(values, options)
          : getSelectedValueNamesInArray(values, options);
      setTooltipContent(content);
    }
  }, [JSON.stringify(values), JSON.stringify(options), isReadOnly]);

  const placeholder = selectPlaceHolder ? selectPlaceHolder : `Search all ${fieldName}`;
  return (
    <StyledTopTooltip
      isEnabled={isReadOnly && tooltipContent}
      tooltipContent={<div>{tooltipContent}</div>}
    >
      <StyledMultiSelectDropdown
        // "selection" is needed for styling ¯\_(ツ)_/¯
        className={`selection ${className}`}
        closeOnChange={false}
        // we're manually handling this, as it wasn't receiving the blur event in
        // some situations (eg: clicking on the search group)
        closeOnBlur={false}
        data-test={MULTI_SELECT_TEST_ID}
        disabled={disabled}
        error={hasError || error}
        fluid
        loading={isLoading}
        open={isOpen}
        ref={dropdownRef}
        selectOnNavigation={false}
        selectOnBlur={false}
        trigger={<MultiSelectTrigger values={values} fieldName={fieldName} />}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <div ref={menuRef} data-test={MULTI_SELECT_MENU_TEST_ID}>
          <SemanticUI.Dropdown.Menu scrolling={false} open={isOpen} className="outer">
            <SemanticUI.Input
              disabled={disabled}
              data-test={MULTI_SELECT_INPUT_TEST_ID}
              icon="search"
              iconPosition="left"
              placeholder={placeholder}
              tabIndex={0}
              ref={inputRef}
              // Semantic UI blocks spaces from being typed into inputs within dropdowns
              onKeyDown={(e) => {
                if (e.keyCode === 32) {
                  e.stopPropagation();
                }
              }}
              onChange={(e) => setSearch(e.target.value)}
              role="searchbox"
            />
            <SemanticUI.Dropdown.Divider />
            <SemanticUI.Dropdown.Menu scrolling open={isOpen} className="inner">
              {options?.length ? (
                options.map((option, i) => (
                  <SemanticUI.Dropdown.Item
                    key={option.key}
                    {...option}
                    active={getIsItemActive(option)}
                    selected={selectedIndex === i}
                    onClick={(e, item) => handleItemToggle(item)}
                  >
                    <StyledCheckableOption
                      isSelected={getIsItemActive(option)}
                      role="null"
                      tagName="div"
                    >
                      <StyledOptionContainer>
                        {option.name}
                        <SecondaryOptionText>
                          {field === EXIT_RULE_ID ? option.id : `ID: ${option.id}`}
                        </SecondaryOptionText>
                      </StyledOptionContainer>
                    </StyledCheckableOption>
                  </SemanticUI.Dropdown.Item>
                ))
              ) : (
                <div className="message">No results found.</div>
              )}
            </SemanticUI.Dropdown.Menu>
          </SemanticUI.Dropdown.Menu>
        </div>
      </StyledMultiSelectDropdown>
    </StyledTopTooltip>
  );
};

const CampaignMultiSelect = (props) => {
  return <MultiSelectDropdown {...props} hookfn={useGetCampaignsSelectOptions} />;
};
const JourneyMetadataMultiSelect = (props) => {
  return <MultiSelectDropdown {...props} hookfn={useGetJourneyMetadataSelectOptions} />;
};
const ExitRulesMultiSelect = (props) => {
  return (
    <MultiSelectDropdown {...props} hookfn={useGetExitRulesSelectOptions} shouldUseLocalSearch />
  );
};

const MultiSelectDropdownHOC = (props) => {
  const field = props.field;
  if (field === null || field === undefined) {
    return null;
  }
  if (field === c.WORKFLOW) {
    return <JourneyMetadataMultiSelect {...props} />;
  } else if (field === c.EXIT_RULE_ID) {
    return <ExitRulesMultiSelect {...props} />;
  } else {
    // this is how the original worked, we would handle campaign and campaign fields like this.
    return <CampaignMultiSelect {...props} />;
  }
};

export default MultiSelectDropdownHOC;
